import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Alert,
  FormHelperText,
  FormLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Assignment,
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon,
  Info,
  ShoppingCart,
  Payment,
  LocationOn,
  WifiTethering,
  Error as ErrorIcon,
  Visibility,
  VisibilityOff,
  Map as MapIcon,
  Close as CloseIcon,
  NavigateBefore,
  NavigateNext,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Contract, Planos, Client } from '../../types';
import SelectEstadosBr from '../selects/EstadosBrSelect';
import GatewaySelect from '../selects/GatewaySelect';
import EscritorioSelect from '../selects/EscritorioSelect';
import FuncionariosSelect from '../selects/FuncionariosSelect';
import PlanosSelect from '../selects/PlanosSelect';
import NasSelect from '../selects/NasSelect';
import DiasVencimentoSelect from '../selects/DiasVencimentoSelect';
import CfopSelect from '../selects/CfopSelect';
import BairroSelect from '../selects/BairroSelect';
import { MapSelectorModal } from '../common';
import { planosService } from '../../services/planosService';
import { clientService } from '../../services/clientService';
import ContratoServicosService from '../../services/contratoServicosService';
import templateService, { Template } from '../../services/templateService';
import { useAuth } from '../../contexts/AuthContext';

// Componente TabPanel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contract-tabpanel-${index}`}
      aria-labelledby={`contract-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Funções de formatação
const formatCurrencyInput = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  if (!numericValue) return '';
  const cents = parseInt(numericValue);
  const reais = cents / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(reais);
};

const parseCurrencyInput = (formattedValue: string): number => {
  const numericString = formattedValue
    .replace(/[R$\s.]/g, '')
    .replace(',', '.');
  return parseFloat(numericString) || 0;
};

const formatCEP = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  if (numericValue.length <= 5) {
    return numericValue;
  }
  return `${numericValue.slice(0, 5)}-${numericValue.slice(5, 8)}`;
};

const parseCEP = (value: string): string => {
  return value.replace(/\D/g, '');
};

const getCurrentDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Tipo do formulário (estende Contract com campo auxiliar de UI)
interface ContractFormData extends Partial<Contract> {
  usar_padrao_corte?: boolean;
  coordenadas_inst?: string;
}

// Schema de validação para CADASTRO de contratos (sem serviço)
const contractCreateSchema = yup.object().shape({
  titulo: yup.string().required('Título é obrigatório').max(255, 'Máximo 255 caracteres'),
  escritorio_id: yup.number().required('Escritório é obrigatório'),
  data_contrato: yup.string().required('Data do contrato é obrigatória'),
  dia_vencimento: yup.number().required('Dia de vencimento é obrigatório').min(1).max(31),
  fidelidade: yup.number().required('Fidelidade é obrigatória').min(0),
  usar_padrao_corte: yup.boolean().optional(),
  dias_corte: yup
    .number()
    .nullable()
    .when('usar_padrao_corte', {
      is: true,
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) => schema.required('Dias para corte é obrigatório').min(0),
    }),
  tx_instalacao: yup.number().required('Taxa de instalação é obrigatória').min(0),
  valor_contratado: yup.number().required('Valor do contrato é obrigatório').min(0, 'Valor do contrato deve ser maior ou igual a zero'),
  acrescimo: yup.number().required('Acréscimo é obrigatório').min(0),
  desconto: yup.number().required('Desconto é obrigatório').min(0),
  cep_inst: yup.string().required('CEP de instalação é obrigatório'),
  cidade_inst: yup.string().required('Cidade de instalação é obrigatória'),
  estado_inst: yup.string().required('Estado de instalação é obrigatório').length(2),
  bairro_inst: yup.string().required('Bairro de instalação é obrigatório'),
  endereco_inst: yup.string().required('Endereço de instalação é obrigatório'),
  codigo_municipio: yup.string().nullable().optional(),
  data_instalacao: yup.string().required('Data de instalação é obrigatória'),
  plano_id: yup.number().required('Plano é obrigatório'),
  usuario_pppoe: yup.string().required('Usuário PPPoE é obrigatório').min(3, 'Usuário PPPoE deve ter pelo menos 3 caracteres'),
  senha_pppoe: yup.string().required('Senha PPPoE é obrigatória').min(3, 'Senha PPPoE deve ter pelo menos 3 caracteres'),
  endereco_ip: yup.string().optional().test(
    'formato-ip',
    'Endereço IP deve ser um IPv4 ou IPv6 válido',
    (value) => {
      if (!value || value.trim() === '') return true; // Permite campo vazio
      const ipv4Regex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
      return ipv4Regex.test(value) || ipv6Regex.test(value);
    }
  ),
  endereco_mac: yup.string().optional().test(
    'formato-mac',
    'Endereço MAC deve estar no formato XX:XX:XX:XX:XX:XX',
    (value) => {
      if (!value || value.trim() === '') return true; // Permite campo vazio
      return /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(value);
    }
  ),
  pool: yup.string().optional(),
  nas_server: yup.number().typeError('Servidor NAS é obrigatório').required('Servidor NAS é obrigatório').min(1, 'Servidor NAS é obrigatório'),
  gateway_id: yup.number().nullable().optional(),
  modalidade: yup.string().required('Modalidade é obrigatória').oneOf(['pre-pago', 'pos-pago']),
  tipo_meio: yup.string().required('Tipo de meio é obrigatório').oneOf(['fibra', 'radio', 'cabo_coaxial', 'par_metalico', 'satelite'], 'Selecione o tipo de meio'),
  acessos_simultaneos: yup.boolean().required(),
  tipo_moradia: yup.string().nullable().optional(),
  contrato_template: yup.number().required('Template de contrato é obrigatório').min(1, 'Selecione um template de contrato'),
  vendedor_id: yup.number().nullable().test(
    'is-defined',
    'Selecione um vendedor ou "Não Informado"',
    (value) => value !== undefined
  ),
});

// Schema de validação para CADASTRO de contratos COM serviço (sem plano e conexão)
const contractCreateWithServicoSchema = yup.object().shape({
  titulo: yup.string().required('Título é obrigatório').max(255, 'Máximo 255 caracteres'),
  escritorio_id: yup.number().required('Escritório é obrigatório'),
  data_contrato: yup.string().required('Data do contrato é obrigatória'),
  dia_vencimento: yup.number().required('Dia de vencimento é obrigatório').min(1).max(31),
  fidelidade: yup.number().required('Fidelidade é obrigatória').min(0),
  usar_padrao_corte: yup.boolean().optional(),
  dias_corte: yup
    .number()
    .nullable()
    .when('usar_padrao_corte', {
      is: true,
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) => schema.required('Dias para corte é obrigatório').min(0),
    }),
  tx_instalacao: yup.number().required('Taxa de instalação é obrigatória').min(0),
  valor_contratado: yup.number().required('Valor do contrato é obrigatório').min(0, 'Valor do contrato deve ser maior ou igual a zero'),
  acrescimo: yup.number().required('Acréscimo é obrigatório').min(0),
  desconto: yup.number().required('Desconto é obrigatório').min(0),
  cep_inst: yup.string().required('CEP de instalação é obrigatório'),
  cidade_inst: yup.string().required('Cidade de instalação é obrigatória'),
  estado_inst: yup.string().required('Estado de instalação é obrigatório').length(2),
  bairro_inst: yup.string().required('Bairro de instalação é obrigatório'),
  endereco_inst: yup.string().required('Endereço de instalação é obrigatório'),
  codigo_municipio: yup.string().nullable().optional(),
  data_instalacao: yup.string().required('Data de instalação é obrigatória'),
  plano_id: yup.number().optional(),
  usuario_pppoe: yup.string().optional(),
  senha_pppoe: yup.string().optional(),
  endereco_ip: yup.string().optional().test(
    'formato-ip',
    'Endereço IP deve ser um IPv4 ou IPv6 válido',
    (value) => {
      if (!value || value.trim() === '') return true; // Permite campo vazio
      const ipv4Regex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
      return ipv4Regex.test(value) || ipv6Regex.test(value);
    }
  ),
  endereco_mac: yup.string().optional().test(
    'formato-mac',
    'Endereço MAC deve estar no formato XX:XX:XX:XX:XX:XX',
    (value) => {
      if (!value || value.trim() === '') return true; // Permite campo vazio
      return /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(value);
    }
  ),
  pool: yup.string().optional(),
  nas_server: yup.number().typeError('Servidor NAS é obrigatório').required('Servidor NAS é obrigatório').min(1, 'Servidor NAS é obrigatório'),
  gateway_id: yup.number().nullable().optional(),
  modalidade: yup.string().required('Modalidade é obrigatória').oneOf(['pre-pago', 'pos-pago']),
  acessos_simultaneos: yup.boolean().optional(),
  tipo_moradia: yup.string().nullable().optional(),
  contrato_template: yup.number().required('Template de contrato é obrigatório').min(1, 'Selecione um template de contrato'),
  vendedor_id: yup.number().nullable().test(
    'is-defined',
    'Selecione um vendedor ou "Não Informado"',
    (value) => value !== undefined
  ),
});

interface TabbedContractFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Contract>) => void;
  clientId: number;
  loading?: boolean;
  servicoId?: number;
}

const TabbedContractForm: React.FC<TabbedContractFormProps> = ({
  open,
  onClose,
  onSubmit,
  clientId,
  loading = false,
  servicoId,
}) => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = React.useState(0);
  const [showPassword, setShowPassword] = React.useState(false);

  // Estados para modal do mapa
  const [mapModalOpen, setMapModalOpen] = React.useState(false);
  const [selectedLocation, setSelectedLocation] = React.useState<{ lat: number, lng: number } | null>(null);

  // Estados para dados do cliente
  const [clientData, setClientData] = React.useState<Client | null>(null);
  const [clientLoading, setClientLoading] = React.useState(false);
  const [useClientAddress, setUseClientAddress] = React.useState(false);

  // Estado para dados do serviço (quando servicoId for fornecido)
  const [servicoData, setServicoData] = React.useState<any>(null);

  // Estado para templates de contrato
  const [contratoTemplates, setContratoTemplates] = React.useState<Template[]>([]);
  const [contratoTemplatesLoading, setContratoTemplatesLoading] = React.useState(false);

  // Estados para planos (não mais necessários - filtragem feita no PlanosSelect)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    setError,
    formState: { errors },
  } = useForm<ContractFormData>({
    resolver: yupResolver(
      (servicoId ? contractCreateWithServicoSchema : contractCreateSchema) as any
    ) as any,
    defaultValues: {
      titulo: '',
      cliente_id: clientId,
      escritorio_id: undefined,
      status: 'ativo',
      plano_id: undefined,
      data_contrato: getCurrentDate(),
      data_instalacao: getCurrentDate(),
      dia_vencimento: undefined as any,
      fidelidade: 12,
      isento: false,
      sms_lembrete: false,
      gerar_fatura_automatico: false,
      geranfcom: false,
      pgcorte: true,
      dias_corte: undefined,
      usar_padrao_corte: true,
      tx_instalacao: 0,
      valor_contratado: undefined,
      acrescimo: 0,
      desconto: 0,
      cep_inst: '',
      cidade_inst: '',
      estado_inst: '',
      bairro_inst: '',
      endereco_inst: '',
      numero_inst: '',
      codigo_municipio: '',
      complemento_inst: '',
      coordenadas_inst: '',
      referencia_inst: '',
      nas_server: undefined as any,
      usuario_pppoe: '',
      senha_pppoe: '',
      endereco_ip: '',
      endereco_mac: '',
      pool: '',
      gateway_id: undefined,
      cfop: '5307',
      modalidade: undefined,
      tipo_meio: undefined,
      acessos_simultaneos: false,
      tipo_moradia: undefined as 'propria' | 'alugado' | undefined,
      contrato_template: undefined as number | undefined,
      vendedor_id: user?.funcionario_id || undefined,
    },
  });

  // Observar mudanças no campo plano_id para atualizar o valor do contrato
  const watchedPlanId = watch('plano_id');

  // Observar mudanças no campo escritorio_id
  const watchedEscritorioId = watch('escritorio_id');
  const watchedCidadeInst = watch('cidade_inst');
  const watchedEstadoInst = watch('estado_inst');

  // Função para filtrar planos por escritório (removida - agora feita no PlanosSelect)

  // Limpar plano selecionado quando o escritório mudar
  React.useEffect(() => {
    if (watchedEscritorioId !== undefined) {
      setValue('plano_id', undefined);
      setValue('valor_contratado', undefined);
    }
  }, [watchedEscritorioId, setValue]);





  // Função para atualizar o valor do contrato quando o plano mudar
  React.useEffect(() => {
    if (watchedPlanId) {
      // Buscar o plano selecionado diretamente do serviço
      const fetchPlanValue = async () => {
        try {
          const plan = await planosService.getById(watchedPlanId);
          if (plan) {
            setValue('valor_contratado', plan.valor);
          }
        } catch (error) {
          console.error('Erro ao buscar valor do plano:', error);
        }
      };

      fetchPlanValue();
    }
  }, [watchedPlanId, setValue]);

  // Função para carregar dados do cliente
  const loadClientData = React.useCallback(async () => {
    try {
      setClientLoading(true);
      const client = await clientService.getById(clientId);
      setClientData(client);
    } catch (error) {
      console.error('Erro ao carregar dados do cliente:', error);
      setClientData(null);
    } finally {
      setClientLoading(false);
    }
  }, [clientId]);

  // Função para carregar todos os planos (removida - agora feita no PlanosSelect)

  // Carregar dados do serviço quando servicoId for fornecido
  React.useEffect(() => {
    if (open && servicoId) {
      const loadServicoData = async () => {
        try {
          const response = await ContratoServicosService.getById(servicoId);
          if (response.success && response.data) {
            setServicoData(response.data);
          }
        } catch (error) {
          console.error('Erro ao carregar dados do serviço:', error);
        }
      };
      loadServicoData();
    } else {
      setServicoData(null);
    }
  }, [open, servicoId]);

  // Atualizar campos do formulário quando servicoData for carregado (após o reset)
  React.useEffect(() => {
    if (servicoData && open) {
      // Preencher título com tipo e usuário PPPoE
      if (servicoData.tipo && servicoData.usuario_pppoe) {
        setValue('titulo', `${servicoData.tipo} - ${servicoData.usuario_pppoe}`, { shouldValidate: false });
      }

      // Preencher campos de endereço de instalação
      if (servicoData.cep_inst) setValue('cep_inst', servicoData.cep_inst, { shouldValidate: false });
      if (servicoData.cidade_inst) setValue('cidade_inst', servicoData.cidade_inst, { shouldValidate: false });
      if (servicoData.estado_inst) setValue('estado_inst', servicoData.estado_inst, { shouldValidate: false });
      if (servicoData.bairro_inst) setValue('bairro_inst', servicoData.bairro_inst, { shouldValidate: false });
      if (servicoData.endereco_inst) setValue('endereco_inst', servicoData.endereco_inst, { shouldValidate: false });
      if (servicoData.numero_inst) setValue('numero_inst', servicoData.numero_inst, { shouldValidate: false });
      if (servicoData.codigo_municipio) setValue('codigo_municipio', servicoData.codigo_municipio, { shouldValidate: false });
      if (servicoData.complemento_inst) setValue('complemento_inst', servicoData.complemento_inst, { shouldValidate: false });
      if (servicoData.coordenadas_inst) setValue('coordenadas_inst', servicoData.coordenadas_inst, { shouldValidate: false });

      // Converter valores para número, tratando null, undefined e strings
      const valorServico = Number(servicoData.valor) || 0;
      const acrescimoServico = Number(servicoData.acrescimo) || 0;
      const descontoServico = Number(servicoData.desconto) || 0;
      const valorTotal = valorServico + acrescimoServico - descontoServico;

      // Verificar se o valor é válido antes de definir
      if (!isNaN(valorTotal) && isFinite(valorTotal)) {
        setValue('valor_contratado', valorTotal, { shouldValidate: false });
      }
    }
  }, [servicoData, open, setValue]);

  // Carregar dados do cliente quando o componente abrir
  React.useEffect(() => {
    if (open && clientId) {
      loadClientData();
    }
  }, [open, clientId, loadClientData]);

  // Carregar templates de contrato
  React.useEffect(() => {
    if (open) {
      const loadTemplates = async () => {
        setContratoTemplatesLoading(true);
        try {
          const response = await templateService.getTemplates({
            type: 'contrato',
            isActive: true,
            limit: 100,
          });
          setContratoTemplates(response.templates || []);
        } catch (err) {
          console.error('Erro ao carregar templates de contrato:', err);
          setContratoTemplates([]);
        } finally {
          setContratoTemplatesLoading(false);
        }
      };
      loadTemplates();
    }
  }, [open]);

  // Definir escritorio_id automaticamente baseado no cliente
  React.useEffect(() => {
    if (clientData && clientData.escritorio_id) {
      setValue('escritorio_id', clientData.escritorio_id);
    }
  }, [clientData, setValue]);

  // Resetar aba ao abrir
  React.useEffect(() => {
    if (open) setTabValue(0);
  }, [open]);

  // Reset do formulário ao abrir (cadastro)
  React.useEffect(() => {
    if (open) {
      // Resetar para valores padrão quando não estiver editando
      // Se servicoId estiver presente, carregar valor do serviço
      const resetData: any = {
        titulo: '',
        cliente_id: clientId,
        escritorio_id: undefined, // Será definido automaticamente baseado no cliente
        status: 'ativo',
        plano_id: undefined,
        data_contrato: getCurrentDate(),
        data_instalacao: getCurrentDate(),
        dia_vencimento: undefined,
        fidelidade: 12,
        isento: false,
        sms_lembrete: false,
        gerar_fatura_automatico: false,
        geranfcom: false,
        pgcorte: true,
        dias_corte: undefined,
        usar_padrao_corte: true,
        tx_instalacao: 0,
        valor_contratado: undefined,
        acrescimo: 0,
        desconto: 0,
        cep_inst: '',
        cidade_inst: '',
        estado_inst: '',
        bairro_inst: '',
        endereco_inst: '',
        numero_inst: '',
        codigo_municipio: '',
        complemento_inst: '',
        coordenadas_inst: '',
        referencia_inst: '',
        nas_server: undefined as any,
        usuario_pppoe: '',
        senha_pppoe: '',
        endereco_ip: '',
        endereco_mac: '',
        pool: '',
        gateway_id: undefined,
        cfop: '5307',
        modalidade: '',
        tipo_meio: undefined,
        acessos_simultaneos: false,
        tipo_moradia: undefined,
        contrato_template: undefined,
        vendedor_id: user?.funcionario_id || undefined,
      };

      // Se servicoData já foi carregado, usar os dados do serviço no reset
      if (servicoData) {
        // Preencher título com tipo e usuário PPPoE
        if (servicoData.tipo && servicoData.usuario_pppoe) {
          resetData.titulo = `${servicoData.tipo} - ${servicoData.usuario_pppoe}`;
        }

        // Preencher campos de endereço de instalação
        if (servicoData.cep_inst) resetData.cep_inst = servicoData.cep_inst;
        if (servicoData.cidade_inst) resetData.cidade_inst = servicoData.cidade_inst;
        if (servicoData.estado_inst) resetData.estado_inst = servicoData.estado_inst;
        if (servicoData.bairro_inst) resetData.bairro_inst = servicoData.bairro_inst;
        if (servicoData.endereco_inst) resetData.endereco_inst = servicoData.endereco_inst;
        if (servicoData.numero_inst) resetData.numero_inst = servicoData.numero_inst;
        if (servicoData.codigo_municipio) resetData.codigo_municipio = servicoData.codigo_municipio;
        if (servicoData.complemento_inst) resetData.complemento_inst = servicoData.complemento_inst;
        if (servicoData.coordenadas_inst) resetData.coordenadas_inst = servicoData.coordenadas_inst;

        // Converter valores para número, tratando null, undefined e strings
        const valorServico = Number(servicoData.valor) || 0;
        const acrescimoServico = Number(servicoData.acrescimo) || 0;
        const descontoServico = Number(servicoData.desconto) || 0;
        const valorTotal = valorServico + acrescimoServico - descontoServico;

        // Verificar se o valor é válido antes de definir
        if (!isNaN(valorTotal) && isFinite(valorTotal)) {
          resetData.valor_contratado = valorTotal;
        }
      }

      reset(resetData);
    }
  }, [open, reset, clientId, servicoId, servicoData, setValue, user]);

  // Função para copiar endereço do cliente
  const handleUseClientAddress = (checked: boolean) => {
    setUseClientAddress(checked);

    if (checked && clientData) {
      setValue('cep_inst', clientData.cep || '');
      setValue('cidade_inst', clientData.cidade || '');
      setValue('estado_inst', clientData.estado || '');
      setValue('bairro_inst', clientData.bairro || '');
      setValue('endereco_inst', clientData.endereco || '');
      setValue('numero_inst', clientData.numero || '');
      setValue('codigo_municipio', clientData.codigo_municipio || '');
      setValue('complemento_inst', clientData.complemento || '');
      setValue('referencia_inst', clientData.referencia || '');
      if (clientData.tipo_moradia) setValue('tipo_moradia', clientData.tipo_moradia);
      setValue('plano_id', undefined);
    }
  };

  const handleFormSubmit = (data: ContractFormData) => {
    const { usar_padrao_corte, ...rest } = data;
    const submissionData: Partial<Contract> = {
      ...rest,
      cliente_id: clientId,
      dias_corte: (usar_padrao_corte ? null : (rest.dias_corte ?? null)) as unknown as number | undefined,
      contrato_template: rest.contrato_template ?? null,
    };
    if (servicoId) {
      (submissionData as any).servico_id = servicoId;
    }
    onSubmit(submissionData);
  };

  // Função para encontrar a aba que contém campos com erro
  const findTabWithErrors = (errors: any): number => {
    // Aba 0: Informações Básicas
    const basicInfoFields = ['titulo', 'data_contrato', 'data_instalacao', 'fidelidade', 'dias_corte', 'sms_lembrete', 'pgcorte', 'contrato_template'];
    if (basicInfoFields.some(field => errors[field])) {
      return 0;
    }

    // Aba 1: Endereço
    const addressFields = ['cep_inst', 'cidade_inst', 'estado_inst', 'bairro_inst', 'endereco_inst'];
    if (addressFields.some(field => errors[field])) {
      return 1;
    }

    // Quando servicoId está presente (transferência), as abas Plano e Conexão não são visíveis
    if (servicoId) {
      // Aba 2: Financeiro (transferência)
      const valueFields = ['dia_vencimento', 'valor_contratado', 'tx_instalacao', 'acrescimo', 'desconto', 'isento', 'gerar_fatura_automatico', 'geranfcom', 'gateway_id', 'vendedor_id', 'cfop', 'modalidade'];
      if (valueFields.some(field => errors[field])) {
        return 2;
      }
    } else {
      // Aba 2: Plano (apenas ao cadastrar sem serviço)
      const planFields = ['plano_id'];
      if (planFields.some(field => errors[field])) {
        return 2;
      }

      // Aba 3: Conexão (apenas ao cadastrar sem serviço)
      const connectionFields = ['usuario_pppoe', 'senha_pppoe', 'endereco_ip', 'endereco_mac', 'pool', 'nas_server'];
      if (connectionFields.some(field => errors[field])) {
        return 3;
      }

      // Aba 4: Financeiro (apenas ao cadastrar sem serviço)
      const valueFields = ['dia_vencimento', 'valor_contratado', 'tx_instalacao', 'acrescimo', 'desconto', 'isento', 'gerar_fatura_automatico', 'geranfcom', 'gateway_id', 'vendedor_id', 'cfop', 'modalidade'];
      if (valueFields.some(field => errors[field])) {
        return 4;
      }
    }

    // Se não encontrou erros específicos, retorna a aba atual
    return tabValue;
  };

  // Mapeamento de campos por aba (para validação ao avançar)
  const getFieldsForTab = (tabIndex: number): string[] => {
    if (servicoId) {
      const tabFields = [
        ['titulo', 'escritorio_id', 'data_contrato', 'data_instalacao', 'fidelidade', 'dias_corte', 'sms_lembrete', 'pgcorte', 'contrato_template'],
        ['cep_inst', 'cidade_inst', 'estado_inst', 'bairro_inst', 'endereco_inst', 'tipo_moradia'],
        ['dia_vencimento', 'valor_contratado', 'tx_instalacao', 'acrescimo', 'desconto', 'isento', 'gerar_fatura_automatico', 'geranfcom', 'gateway_id', 'vendedor_id', 'cfop', 'modalidade'],
      ];
      return tabIndex >= 0 && tabIndex < tabFields.length ? tabFields[tabIndex] : [];
    }
    const tabFields = [
      ['titulo', 'escritorio_id', 'data_contrato', 'data_instalacao', 'fidelidade', 'dias_corte', 'sms_lembrete', 'pgcorte', 'contrato_template'],
      ['cep_inst', 'cidade_inst', 'estado_inst', 'bairro_inst', 'endereco_inst', 'tipo_moradia'],
      ['plano_id'],
      ['usuario_pppoe', 'senha_pppoe', 'endereco_ip', 'endereco_mac', 'pool', 'nas_server', 'tipo_meio'],
      ['dia_vencimento', 'valor_contratado', 'tx_instalacao', 'acrescimo', 'desconto', 'isento', 'gerar_fatura_automatico', 'geranfcom', 'gateway_id', 'cfop', 'modalidade'],
    ];
    return tabIndex >= 0 && tabIndex < tabFields.length ? tabFields[tabIndex] : [];
  };

  // Função para verificar se uma aba específica tem erros
  const hasTabErrors = (tabIndex: number, errors: any): boolean => {
    const fieldsInThisTab = getFieldsForTab(tabIndex);
    if (fieldsInThisTab.length === 0) return false;
    return fieldsInThisTab.some(field => errors[field]);
  };

  // Índice da última aba (depende do modo edição/transferência)
  const maxTabIndex = servicoId ? 2 : 4;
  const isLastTab = tabValue >= maxTabIndex;
  const isFirstTab = tabValue <= 0;

  // Função auxiliar para validar campos antes de avançar aba
  const validateCurrentTab = async (): Promise<boolean> => {
    const fields = getFieldsForTab(tabValue);
    let valid = fields.length === 0 || await trigger(fields as any);

    // Se a aba estiver válida e for a aba de Conexão (3), fazer a verificação de unicidade
    if (valid && tabValue === 3 && !servicoId) {
      const usuario_pppoe = watch('usuario_pppoe');
      const endereco_ip = watch('endereco_ip');
      const endereco_mac = watch('endereco_mac');

      if (usuario_pppoe || endereco_ip || endereco_mac) {
        try {
          const res = await ContratoServicosService.checkUnique({
            usuario_pppoe: usuario_pppoe || undefined,
            endereco_ip: endereco_ip || undefined,
            endereco_mac: endereco_mac || undefined,
          });

          if (res.data && !res.data.unique) {
            if (res.data.field) {
              setError(res.data.field as any, { type: 'manual', message: res.data.message });
            }
            valid = false;
          }
        } catch (err) {
          console.error('Erro ao verificar unicidade:', err);
        }
      }
    }

    return valid;
  };

  // Avançar para próxima aba somente se a aba atual estiver válida
  const handleNextTab = async () => {
    const valid = await validateCurrentTab();
    if (valid && tabValue < maxTabIndex) {
      setTabValue(tabValue + 1);
      const dialogContent = document.querySelector('[role="tabpanel"]:not([hidden])');
      if (dialogContent) dialogContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Voltar para aba anterior (sem validação)
  const handlePrevTab = () => {
    if (tabValue > 0) {
      setTabValue(tabValue - 1);
      const dialogContent = document.querySelector('[role="tabpanel"]:not([hidden])');
      if (dialogContent) dialogContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleClose = (event?: any, reason?: string) => {
    if (reason === 'backdropClick') return;
    reset();
    setUseClientAddress(false);
    setClientData(null);
    setTabValue(0);
    setShowPassword(false);
    onClose();
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    if (newValue > tabValue) {
      // Avançar: só permite se a aba atual estiver válida
      const valid = await validateCurrentTab();
      if (valid) setTabValue(newValue);
    } else {
      // Voltar: permite sempre
      setTabValue(newValue);
    }
  };

  const handleGatewayChange = (gatewayId: number | null) => {
    setValue('gateway_id', gatewayId || undefined);
  };

  // Funções para modal do mapa
  const handleOpenMapModal = () => {
    // Se já tem coordenadas, usar como ponto inicial
    const currentCoords = watch('coordenadas_inst');
    if (currentCoords && currentCoords.includes(',')) {
      const [lat, lng] = currentCoords.split(',').map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        setSelectedLocation({ lat, lng });
      } else {
        setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
      }
    } else {
      setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
    }
    setMapModalOpen(true);
  };

  const handleCloseMapModal = () => {
    setMapModalOpen(false);
  };

  const handleConfirmMapLocation = (lat: number, lng: number) => {
    const coordString = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    setValue('coordenadas_inst', coordString);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <form onSubmit={handleSubmit(handleFormSubmit, (errors) => {
          const errorTabIndex = findTabWithErrors(errors);
          setTabValue(errorTabIndex);
          const panel = document.querySelector('[role="tabpanel"]:not([hidden])');
          if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })}>
          <DialogContent sx={{ pb: 0 }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="Formulário de contrato"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 40,
                  // Centralizar apenas em telas maiores que mobile
                  '& .MuiTabs-scroller': {
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', md: 'center' }
                  },
                  '& .MuiTabs-flexContainer': {
                    justifyContent: { xs: 'flex-start', md: 'center' }
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  },
                  '& .MuiTab-root': {
                    minHeight: 40,
                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                    padding: { xs: '6px 8px', md: '6px 12px' },
                    margin: '4px 2px 0 2px',
                    borderRadius: '8px 8px 0 0',
                    backgroundColor: 'transparent',
                    color: 'text.secondary',
                    transition: 'all 0.2s ease',
                    minWidth: { xs: 'auto', md: 'auto' },
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      color: 'primary.main'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: 'primary.main',
                      fontWeight: 600
                    }
                  }
                }}
              >
                <Tab
                  icon={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Info />
                      {hasTabErrors(0, errors) && (
                        <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      )}
                    </Box>
                  }
                  label="Informações Básicas"
                  id="contract-tab-0"
                  aria-controls="contract-tabpanel-0"
                  sx={{
                    color: hasTabErrors(0, errors) ? 'error.main' : undefined,
                  }}
                />
                <Tab
                  icon={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn />
                      {hasTabErrors(1, errors) && (
                        <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      )}
                    </Box>
                  }
                  label="Endereço de Instalação"
                  id="contract-tab-1"
                  aria-controls="contract-tabpanel-1"
                  sx={{
                    color: hasTabErrors(1, errors) ? 'error.main' : undefined,
                  }}
                />
                {/* Aba Plano - apenas visível ao cadastrar sem serviço */}
                {!servicoId && (
                  <Tab
                    icon={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ShoppingCart />
                        {hasTabErrors(2, errors) && (
                          <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        )}
                      </Box>
                    }
                    label="Plano"
                    id="contract-tab-2"
                    aria-controls="contract-tabpanel-2"
                    sx={{
                      color: hasTabErrors(2, errors) ? 'error.main' : undefined,
                    }}
                  />
                )}
                {/* Aba Conexão - apenas visível ao cadastrar sem serviço */}
                {!servicoId && (
                  <Tab
                    icon={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WifiTethering />
                        {hasTabErrors(3, errors) && (
                          <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        )}
                      </Box>
                    }
                    label="Conexão"
                    id="contract-tab-3"
                    aria-controls="contract-tabpanel-3"
                    sx={{
                      color: hasTabErrors(3, errors) ? 'error.main' : undefined,
                    }}
                  />
                )}
                <Tab
                  icon={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Payment />
                      {hasTabErrors(servicoId ? 2 : 4, errors) && (
                        <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      )}
                    </Box>
                  }
                  label="Financeiro"
                  id={servicoId ? "contract-tab-2" : "contract-tab-4"}
                  aria-controls={servicoId ? "contract-tabpanel-2" : "contract-tabpanel-4"}
                  sx={{
                    color: hasTabErrors(servicoId ? 2 : 4, errors) ? 'error.main' : undefined,
                  }}
                />
              </Tabs>
            </Box>

            {/* Aba 1: Informações Básicas */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="escritorio_id"
                    control={control}
                    render={({ field }) => (
                      <EscritorioSelect
                        value={field.value || null}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        label="Escritório"
                        size="small"
                        required={true}
                        allowClear={false}
                        multiple={false}
                        onlyActive={true}
                        error={!!errors.escritorio_id}
                        helperText={errors.escritorio_id?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Controller
                    name="titulo"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Título do Contrato"
                        size="small"
                        error={!!errors.titulo}
                        helperText={errors.titulo?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="data_contrato"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Data do Contrato"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        error={!!errors.data_contrato}
                        helperText={errors.data_contrato?.message}
                        inputProps={{ min: "1900-01-01", max: "2999-12-31" }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="data_instalacao"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Data de Instalação"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        error={!!errors.data_instalacao}
                        helperText={errors.data_instalacao?.message}
                        inputProps={{ min: "1900-01-01", max: "2999-12-31" }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="fidelidade"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Fidelidade (meses) 0 para nao"
                        type="number"
                        inputProps={{ min: 0 }}
                        size="small"
                        error={!!errors.fidelidade}
                        helperText={errors.fidelidade?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Campos movidos da aba Configurações */}
                <Grid item xs={12} md={3}>
                  <Controller
                    name="dias_corte"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Dias para Corte"
                        type="number"
                        inputProps={{ min: 0 }}
                        size="small"
                        disabled={watch('usar_padrao_corte')}
                        error={!!errors.dias_corte}
                        helperText={errors.dias_corte?.message}
                      />
                    )}
                  />
                  <Controller
                    name="usar_padrao_corte"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            checked={!!field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              if (e.target.checked) {
                                setValue('dias_corte', undefined as unknown as number | undefined);
                              }
                            }}
                          />
                        }
                        label="Padrão do sistema"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="pgcorte"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Pgcorte"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="contrato_template"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!errors.contrato_template} required>
                        <InputLabel>Modelo de Contrato *</InputLabel>
                        <Select
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                          label="Modelo de Contrato *"
                          disabled={contratoTemplatesLoading}
                          displayEmpty
                          renderValue={(v) => {
                            if (!v) return '';
                            const t = contratoTemplates.find((x) => x.id === v);
                            return t ? t.title : '';
                          }}
                        >
                          <MenuItem value="">
                            <em>Selecione um modelo</em>
                          </MenuItem>
                          {/* Evita o warning do MUI de valor fora do range durante o loading */}
                          {field.value && !contratoTemplates.some(t => t.id === field.value) && (
                            <MenuItem value={field.value} sx={{ display: 'none' }}>
                              {field.value}
                            </MenuItem>
                          )}
                          {contratoTemplates.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.title}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors.contrato_template?.message || 'Modelo padrão para impressão do contrato'}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>

              </Grid>
            </TabPanel>

            {/* Aba 2: Endereço de Instalação */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useClientAddress}
                      onChange={(e) => handleUseClientAddress(e.target.checked)}
                      disabled={clientLoading || !clientData}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {clientLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                      Usar endereço do cliente
                    </Box>
                  }
                />
              </Box>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={2.5}>
                  <Controller
                    name="cep_inst"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="CEP"
                        value={field.value ? formatCEP(field.value) : ''}
                        onChange={(e) => {
                          const formattedValue = formatCEP(e.target.value);
                          const parsedValue = parseCEP(formattedValue);
                          field.onChange(parsedValue);
                        }}
                        placeholder="00000-000"
                        inputProps={{ maxLength: 9 }}
                        size="small"
                        error={!!errors.cep_inst}
                        helperText={errors.cep_inst?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={5}>
                  <Controller
                    name="cidade_inst"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Cidade"
                        size="small"
                        error={!!errors.cidade_inst}
                        helperText={errors.cidade_inst?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2.5}>
                  <Controller
                    name="estado_inst"
                    control={control}
                    render={({ field }) => (
                      <SelectEstadosBr
                        value={field.value || ''}
                        onChange={field.onChange}
                        label="Estado"
                        error={!!errors.estado_inst}
                        helperText={errors.estado_inst?.message}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Controller
                    name="codigo_municipio"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Cód. IBGE"
                        size="small"
                        error={!!errors.codigo_municipio}
                        helperText={errors.codigo_municipio?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="bairro_inst"
                    control={control}
                    render={({ field }) => (
                      <BairroSelect
                        value={field.value || null}
                        onChange={(v) => field.onChange(v || '')}
                        label="Bairro"
                        size="small"
                        allowCustom
                        allowClear={false}
                        required
                        error={!!errors.bairro_inst}
                        helperText={errors.bairro_inst?.message}
                        cidade_inst={watchedCidadeInst}
                        estado_inst={watchedEstadoInst}
                        escritorio_id={watchedEscritorioId}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="endereco_inst"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Endereço"
                        size="small"
                        error={!!errors.endereco_inst}
                        helperText={errors.endereco_inst?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Controller
                    name="numero_inst"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Número"
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <Controller
                    name="referencia_inst"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Referência"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Box sx={{ position: 'relative' }}>
                    <Controller
                      name="coordenadas_inst"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Coordenadas (lat,lng)"
                          placeholder="-23.5505,-46.6333"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleOpenMapModal}
                                  size="small"
                                  title="Selecionar no Mapa"
                                >
                                  <MapIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          size="small"
                        />
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="complemento_inst"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Complemento"
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="tipo_moradia"
                    control={control}
                    render={({ field }) => (
                      <FormControl component="fieldset" size="small" error={!!errors.tipo_moradia}>
                        <RadioGroup row {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                          <FormControlLabel value="propria" control={<Radio size="small" />} label="Própria" />
                          <FormControlLabel value="alugado" control={<Radio size="small" />} label="Alugado" />
                        </RadioGroup>
                        {errors.tipo_moradia && (
                          <FormHelperText>{errors.tipo_moradia.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Aba 2: Plano - apenas visível ao cadastrar sem serviço */}
            {!servicoId && (
              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Controller
                      name="plano_id"
                      control={control}
                      render={({ field }) => {
                        const currentEscritorioId = watch('escritorio_id');

                        if (!currentEscritorioId) {
                          return (
                            <FormControl fullWidth disabled>
                              <InputLabel>Plano</InputLabel>
                              <Select value="" label="Plano" disabled>
                                <MenuItem disabled>
                                  Selecione um escritório primeiro (na aba "Informações Básicas")
                                </MenuItem>
                              </Select>
                              <FormHelperText>
                                Selecione um escritório na aba "Informações Básicas" primeiro para ver os planos disponíveis
                              </FormHelperText>
                            </FormControl>
                          );
                        }

                        // Validar se o escritório é um número válido
                        if (typeof currentEscritorioId !== 'number' || currentEscritorioId <= 0) {
                          return (
                            <FormControl fullWidth error>
                              <InputLabel>Plano</InputLabel>
                              <Select value="" label="Plano" disabled>
                                <MenuItem disabled>
                                  Escritório inválido selecionado
                                </MenuItem>
                              </Select>
                              <FormHelperText>
                                Selecione um escritório válido na aba "Informações Básicas"
                              </FormHelperText>
                            </FormControl>
                          );
                        }

                        return (
                          <PlanosSelect
                            value={field.value || null}
                            onChange={(value) => {
                              field.onChange(value);

                              // Atualizar o valor do contrato quando um plano for selecionado
                              if (value && typeof value === 'number') {
                                // Buscar o plano selecionado diretamente do serviço
                                const fetchPlanValue = async () => {
                                  try {
                                    const plan = await planosService.getById(value);
                                    if (plan) {
                                      setValue('valor_contratado', plan.valor);
                                    }
                                  } catch (error) {
                                    console.error('Erro ao buscar valor do plano:', error);
                                  }
                                };

                                fetchPlanValue();
                              } else {
                                setValue('valor_contratado', undefined);
                              }
                            }}
                            label="Plano"
                            size="small"
                            required={true}
                            allowClear={false}
                            multiple={false}
                            onlyActive={true}
                            error={!!errors.plano_id}
                            helperText={errors.plano_id?.message}
                            fullWidth={true}
                            variant="outlined"
                            escritorio_id={currentEscritorioId}
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            )}

            {/* Aba 3: Conexão - apenas visível ao cadastrar sem serviço */}
            {!servicoId && (
              <TabPanel value={tabValue} index={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="usuario_pppoe"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Usuário PPPoE"
                          size="small"
                          error={!!errors.usuario_pppoe}
                          helperText={errors.usuario_pppoe?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Controller
                      name="senha_pppoe"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Senha PPPoE"
                          type={showPassword ? 'text' : 'password'}
                          size="small"
                          error={!!errors.senha_pppoe}
                          helperText={errors.senha_pppoe?.message}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="alternar visibilidade da senha"
                                  onClick={() => setShowPassword(!showPassword)}
                                  onMouseDown={(e) => e.preventDefault()}
                                  edge="end"
                                  size="small"
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="nas_server"
                      control={control}
                      render={({ field }) => (
                        <NasSelect
                          value={field.value ? Number(field.value) : null}
                          onChange={(value) => field.onChange(value)}
                          label="Servidor NAS"
                          size="small"
                          required={true}
                          error={!!errors.nas_server}
                          helperText={errors.nas_server?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Controller
                      name="endereco_ip"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Endereço IP"
                          placeholder="192.168.1.100"
                          size="small"
                          error={!!errors.endereco_ip}
                          helperText={errors.endereco_ip?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Controller
                      name="endereco_mac"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Endereço MAC"
                          placeholder="00:11:22:33:44:55"
                          size="small"
                          error={!!errors.endereco_mac}
                          helperText={errors.endereco_mac?.message}
                          onChange={(e) => {
                            // Formatar automaticamente o MAC address
                            let value = e.target.value.replace(/[^\da-fA-F]/g, '');
                            if (value.length > 12) value = value.slice(0, 12);

                            const formatted = value.match(/.{1,2}/g)?.join(':') || value;
                            field.onChange(formatted);
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="pool"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.pool}>
                          <InputLabel>Pool</InputLabel>
                          <Select {...field} label="Pool" size="small" disabled>
                            <MenuItem value="">
                              <em>Selecione um pool</em>
                            </MenuItem>
                            <MenuItem value="Pool_Residencial">Pool Residencial</MenuItem>
                            <MenuItem value="Pool_Empresarial">Pool Empresarial</MenuItem>
                            <MenuItem value="Pool_Publico">Pool Público</MenuItem>
                            <MenuItem value="Pool_VIP">Pool VIP</MenuItem>
                            <MenuItem value="Pool_Teste">Pool Teste</MenuItem>
                          </Select>
                          {errors.pool && (
                            <FormHelperText>{errors.pool.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="tipo_meio"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.tipo_meio}>
                          <InputLabel>Tipo de Meio</InputLabel>
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            label="Tipo de Meio"
                            onChange={(e) => field.onChange(e.target.value || undefined)}
                          >
                            <MenuItem value="">
                              <em>Selecione o tipo de meio</em>
                            </MenuItem>
                            <MenuItem value="fibra">Fibra Óptica</MenuItem>
                            <MenuItem value="radio">Rádio</MenuItem>
                            <MenuItem value="cabo_coaxial">Cabo Coaxial</MenuItem>
                            <MenuItem value="par_metalico">Par Metálico</MenuItem>
                            <MenuItem value="satelite">Satélite</MenuItem>
                          </Select>
                          {errors.tipo_meio && (
                            <FormHelperText>{errors.tipo_meio.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="acessos_simultaneos"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label="Permitir Acessos Simultâneos"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            )}

            {/* Aba Financeiro - índice 2 ao editar ou transferir serviço, 4 ao cadastrar */}
            <TabPanel value={tabValue} index={servicoId ? 2 : 4}>
              <Grid container spacing={3}>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="valor_contratado"
                    control={control}
                    render={({ field }) => {
                      const getDisplayValue = (): string => {
                        if (field.value === undefined || field.value === null) {
                          return '';
                        }
                        if (field.value === 0) {
                          return 'R$ 0,00';
                        }
                        return new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(field.value);
                      };

                      const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                        const inputValue = event.target.value;

                        // Se o campo estiver vazio, definir como undefined para validação
                        if (!inputValue || inputValue.trim() === '') {
                          field.onChange(undefined);
                          return;
                        }

                        const formattedValue = formatCurrencyInput(inputValue);
                        const numericValue = parseCurrencyInput(formattedValue);
                        field.onChange(numericValue);
                      };

                      return (
                        <TextField
                          fullWidth
                          label="Valor do Contrato"
                          value={getDisplayValue()}
                          onChange={handleValueChange}
                          placeholder="R$ 0,00"
                          size="small"
                          inputProps={{
                            inputMode: 'numeric',
                          }}
                          error={!!errors.valor_contratado}
                          helperText={errors.valor_contratado?.message}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="tx_instalacao"
                    control={control}
                    render={({ field }) => {
                      const getDisplayValue = (): string => {
                        if (!field.value || field.value === 0) {
                          return '';
                        }
                        return new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(field.value);
                      };

                      const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                        const inputValue = event.target.value;
                        const formattedValue = formatCurrencyInput(inputValue);
                        const numericValue = parseCurrencyInput(formattedValue);
                        field.onChange(numericValue);
                      };

                      return (
                        <TextField
                          fullWidth
                          label="Taxa de Instalação"
                          value={getDisplayValue()}
                          onChange={handleValueChange}
                          placeholder="R$ 0,00"
                          size="small"
                          inputProps={{
                            inputMode: 'numeric',
                          }}
                          error={!!errors.tx_instalacao}
                          helperText={errors.tx_instalacao?.message}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="acrescimo"
                    control={control}
                    render={({ field }) => {
                      const getDisplayValue = (): string => {
                        if (!field.value || field.value === 0) {
                          return '';
                        }
                        return new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(field.value);
                      };

                      const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                        const inputValue = event.target.value;
                        const formattedValue = formatCurrencyInput(inputValue);
                        const numericValue = parseCurrencyInput(formattedValue);
                        field.onChange(numericValue);
                      };

                      return (
                        <TextField
                          fullWidth
                          label="Acréscimo"
                          value={getDisplayValue()}
                          onChange={handleValueChange}
                          placeholder="R$ 0,00"
                          size="small"
                          inputProps={{
                            inputMode: 'numeric',
                          }}
                          error={!!errors.acrescimo}
                          helperText={errors.acrescimo?.message}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="desconto"
                    control={control}
                    render={({ field }) => {
                      const getDisplayValue = (): string => {
                        if (!field.value || field.value === 0) {
                          return '';
                        }
                        return new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(field.value);
                      };

                      const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                        const inputValue = event.target.value;
                        const formattedValue = formatCurrencyInput(inputValue);
                        const numericValue = parseCurrencyInput(formattedValue);
                        field.onChange(numericValue);
                      };

                      return (
                        <TextField
                          fullWidth
                          label="Desconto"
                          value={getDisplayValue()}
                          onChange={handleValueChange}
                          placeholder="R$ 0,00"
                          size="small"
                          inputProps={{
                            inputMode: 'numeric',
                          }}
                          error={!!errors.desconto}
                          helperText={errors.desconto?.message}
                        />
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Controller
                    name="dia_vencimento"
                    control={control}
                    render={({ field }) => (
                      <DiasVencimentoSelect
                        value={field.value}
                        onChange={(v) => field.onChange(v === '' ? undefined : v)}
                        error={!!errors.dia_vencimento}
                        helperText={errors.dia_vencimento?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                {/* Modalidade */}
                <Grid item xs={12} md={4}>
                  <Controller
                    name="modalidade"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.modalidade}>
                        <InputLabel>Modalidade</InputLabel>
                        <Select {...field} label="Modalidade" size="small">
                          <MenuItem value="" disabled>
                            <em>Selecione a modalidade</em>
                          </MenuItem>
                          <MenuItem value="pos-pago">
                            Pós-pago{' '}
                            <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                              - Paga depois de usar
                            </Box>
                          </MenuItem>
                          <MenuItem value="pre-pago">
                            Pré-pago{' '}
                            <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                              - Paga antes de usar
                            </Box>
                          </MenuItem>
                        </Select>
                        {errors.modalidade && (
                          <FormHelperText>{errors.modalidade.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Gateway de Pagamento */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="gateway_id"
                    control={control}
                    render={({ field }) => (
                      <GatewaySelect
                        value={field.value || undefined}
                        onChange={handleGatewayChange}
                        label="Gateway de Pagamento"
                        required={false}
                        helperText="Gateway opcional para geração automática de boletos/PIX"
                        showOnlyActive={true}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                {/* Vendedor */}
                <Grid item xs={12} md={5}>
                  <Controller
                    name="vendedor_id"
                    control={control}
                    render={({ field }) => (
                      <FuncionariosSelect
                        value={field.value ?? null}
                        onChange={(id) => field.onChange(id)}
                        label="Vendedor"
                        size="small"
                        placeholder="Selecione o vendedor ou Não Informado"
                        error={!!errors.vendedor_id}
                        helperText={errors.vendedor_id?.message || 'Selecione o funcionário vendedor responsável'}
                        allowClear={true}
                        required={true}
                      />
                    )}
                  />
                </Grid>
                {/* CFOP */}
                <Grid item xs={12} md={7}>
                  <Controller
                    name="cfop"
                    control={control}
                    render={({ field }) => (
                      <CfopSelect
                        value={field.value || undefined}
                        onChange={(v) => field.onChange(v)}
                        error={!!errors.cfop}
                        helperText={errors.cfop?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid display="flex" flexDirection="row" item xs={12} md={12}>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="isento"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label="Isento de Mensalidades"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Controller
                      name="gerar_fatura_automatico"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label="Gerar Fatura Automático"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Controller
                      name="geranfcom"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label="Gerar NFcom"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="sms_lembrete"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label="SMS Lembrete"
                        />
                      )}
                    />
                  </Grid>
                </Grid>




              </Grid>
            </TabPanel>
          </DialogContent>

          <DialogActions sx={{ flexWrap: 'wrap', gap: 0 }}>
            <Button size='small' onClick={handleClose}>
              Cancelar
            </Button>
            <Box sx={{ flex: 1 }} />
            {!isFirstTab && (
              <Button
                type="button"
                size='small'
                onClick={handlePrevTab}
                startIcon={<NavigateBefore />}
              >
                Voltar
              </Button>
            )}
            {!isLastTab && (
              <Button
                type="button"
                size='small'
                variant="outlined"
                onClick={handleNextTab}
                endIcon={<NavigateNext />}
              >
                Próximo
              </Button>
            )}
            {isLastTab && (
              <Button
                type="submit"
                size='small'
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Assignment />}
              >
                {loading ? 'Criando...' : 'Criar Contrato'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal para Seleção de Coordenadas no Mapa */}
      <MapSelectorModal
        open={mapModalOpen}
        onClose={handleCloseMapModal}
        onConfirm={handleConfirmMapLocation}
        initialCoordinates={selectedLocation}
        title="📍 Selecionar Localização no Mapa"
        description="Clique no mapa para selecionar a localização de instalação do contrato"
      />
    </>
  );
};

export default TabbedContractForm;